/**
 * @fileoverview OpsFeedbackGithubPanel — Operations → Feedback → GitHub subtab.
 *
 * Shows the feedback-mirror pipeline state (total submissions, how many are
 * mirrored to a GH issue, how many are still un-mirrored, oldest-unmirrored
 * age) and lets an operator kick off a bounded backfill run without opening a
 * terminal. Wraps api.opsFeedbackGithubStatus / opsFeedbackGithubBackfill.
 *
 * When GITHUB_TOKEN is unset the panel still renders the tallies (so the
 * operator knows what would be mirrored) but the Backfill button is disabled
 * and the tile shows a "GH mirror disabled" chip.
 */

import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

const LIMIT_OPTIONS = [10, 50, 100, 500];

/** Format an ISO timestamp as `YYYY-MM-DD HH:MM UTC`. */
function fmtIsoUtc(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 16)} UTC`;
  } catch (_) {
    return iso;
  }
}

/**
 * Feedback → GitHub subsystem panel.
 *
 * @param {Object} props
 * @param {string} props.adminApiPrefix - Full admin API prefix (`/api/{secret}/admin`).
 * @returns {JSX.Element}
 */
function OpsFeedbackGithubPanel({ adminApiPrefix }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(50);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);

  const fetchStatus = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.opsFeedbackGithubStatus(adminApiPrefix);
      setStatus(data);
    } catch (err) {
      setError('Could not load feedback → GitHub status.');
    } finally {
      setLoading(false);
    }
  }, [adminApiPrefix]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleRun = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const { data } = await api.opsFeedbackGithubBackfill(adminApiPrefix, limit);
      setRunResult({
        ok: true,
        attempted: data.attempted,
        mirrored: data.mirrored,
        failed: data.failed,
      });
      await fetchStatus();
    } catch (err) {
      setRunResult({
        ok: false,
        error: err?.response?.data?.error || 'Backfill failed',
      });
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="ops-panel" data-testid="ops-feedback-github-panel">
        <p role="status">Loading feedback → GitHub status…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ops-panel" data-testid="ops-feedback-github-panel">
        <div className="empty-state" role="alert">
          <p>{error}</p>
          <button type="button" className="btn btn-secondary" onClick={fetchStatus}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const enabled = !!status?.enabled;

  return (
    <div className="ops-panel" data-testid="ops-feedback-github-panel">
      <h2>Feedback → GitHub</h2>
      <p className="ops-panel__blurb">
        Every submitted feedback row is best-effort mirrored to a GitHub Issue
        by <code>services/github_issues.py</code>. This panel surfaces the
        current backlog and lets you catch up any un-mirrored rows without
        SSH-ing into the container.
      </p>

      <div className="card ops-panel__status" data-testid="ops-feedback-github-status">
        <dl className="ops-panel__dl">
          <div>
            <dt>GH mirror</dt>
            <dd>
              <span
                className={`badge ${enabled ? 'badge-success' : 'badge-warning'}`}
                data-testid="ops-feedback-github-enabled"
              >
                {enabled ? 'Enabled' : 'Disabled (GITHUB_TOKEN unset)'}
              </span>
            </dd>
          </div>
          <div>
            <dt>Target repo</dt>
            <dd className="ops-panel__mono">{status?.gh_repo || '—'}</dd>
          </div>
          <div>
            <dt>Total feedback rows</dt>
            <dd className="ops-panel__mono" data-testid="ops-feedback-github-total">
              {(status?.total || 0).toLocaleString()}
            </dd>
          </div>
          <div>
            <dt>Mirrored</dt>
            <dd className="ops-panel__mono" data-testid="ops-feedback-github-mirrored">
              {(status?.mirrored || 0).toLocaleString()}
            </dd>
          </div>
          <div>
            <dt>Un-mirrored</dt>
            <dd
              className={`ops-panel__mono ${(status?.unmirrored || 0) > 0 ? 'ops-panel__mono--warn' : ''}`}
              data-testid="ops-feedback-github-unmirrored"
            >
              {(status?.unmirrored || 0).toLocaleString()}
            </dd>
          </div>
          <div>
            <dt>Oldest un-mirrored</dt>
            <dd>{fmtIsoUtc(status?.oldest_unmirrored_at)}</dd>
          </div>
        </dl>
      </div>

      <div className="ops-panel__actions" data-testid="ops-feedback-github-actions">
        <label className="ops-panel__label" htmlFor="ops-feedback-github-limit">
          Batch size
          <select
            id="ops-feedback-github-limit"
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value, 10))}
            data-testid="ops-feedback-github-limit-select"
            disabled={running || !enabled}
          >
            {LIMIT_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleRun}
          disabled={running || !enabled}
          data-testid="ops-feedback-github-run-btn"
          title={enabled ? '' : 'GITHUB_TOKEN must be set in the container env'}
        >
          {running ? 'Running…' : 'Run backfill now'}
        </button>
      </div>

      {runResult && (
        <div
          className={`ops-panel__result ops-panel__result--${runResult.ok ? 'ok' : 'err'}`}
          role="status"
          data-testid="ops-feedback-github-result"
        >
          {runResult.ok
            ? `Attempted ${runResult.attempted} · mirrored ${runResult.mirrored} · failed ${runResult.failed}`
            : `Failed: ${runResult.error}`}
        </div>
      )}
    </div>
  );
}

export default OpsFeedbackGithubPanel;
